import { Autocomplete, outlinedInputClasses, styled } from '@mui/material';
import React from 'react';
import { TextField } from '../textfield';
import { AutocompleteLoadingIndicator } from '../autocomplete/AutocompleteLoadingIndicator';
import Close from '../icons/Close';
import DrawSearch from '../icons/DrawSearch';
import Search from '../icons/Search';
import { IconButton, IconButtonProps } from '../iconButton';
import { Corners } from '../corners/tokens';
import { SearchFieldColorVariants, SearchFieldProps } from './types';
import { AutocompleteSizes, autocompleteClasses } from '../autocomplete/types';
import { Input } from '../sizes';
import { Primary, Secondary, White } from '../color';
import useSearchField from './useSearchField';
import { Spacing3 } from '../spacing';

interface OwnerState {
  size: AutocompleteSizes;
  colorVariant: SearchFieldColorVariants;
}

const colorVariants: Record<
  'button',
  Record<
    SearchFieldColorVariants,
    Pick<IconButtonProps, 'color' | 'variant'> & { corner: string }
  >
> = {
  button: {
    primary: {
      color: 'primary',
      variant: 'contained',
      corner: Corners.large,
    },
    default: {
      color: 'default',
      variant: 'contained',
      corner: Corners.large,
    },
    integrated: {
      color: 'primary',
      variant: 'text',
      corner: '0px',
    },
  },
};

export const SearchFieldRoot = styled('form')(({
  ownerState: { colorVariant, size },
}: { ownerState: OwnerState }) => ({
  display: 'flex',
  flexDirection: 'row',
  margin: `0 ${Spacing3}`,

  [`.${autocompleteClasses.root} .${outlinedInputClasses.root}`]: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,

    [`.${autocompleteClasses.input}`]: {
      borderRight: 0,
    },

    [`.${autocompleteClasses.popupIndicatorOpen}`]: {
      transform: 'none',
    },

    ...(colorVariant === 'integrated' && {
      [`.${outlinedInputClasses.notchedOutline}`]: {
        zIndex: 1,
        right: -Input[size],
        borderTopRightRadius: Corners.small,
        borderBottomRightRadius: Corners.small,
      },
    }),
  },

  [`.${autocompleteClasses.searchFieldSubmit}`]: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: colorVariants.button[colorVariant]?.corner,
    borderBottomRightRadius: colorVariants.button[colorVariant]?.corner,
    zIndex: 0,
    ...(colorVariant === 'integrated' && {
      width: Input[size],
      height: Input[size],
      backgroundColor: White,
      borderColor: 'pink',
      '&:hover': {
        backgroundColor: Primary.surface.hover,
      },
    }),
  },

  ...(colorVariant === 'integrated' && {
    [`.${autocompleteClasses.focused} + .${autocompleteClasses.searchFieldSubmit}`]: {
      width: Input[size],
      height: Input[size],
    },
    [`&:hover .${autocompleteClasses.root}:not(.${autocompleteClasses.focused}) .${outlinedInputClasses.notchedOutline}`]: {
      borderColor: Secondary.default.hover,
    },
  }),
}));

function SearchField<Value>(props: SearchFieldProps<Value>) {
  const {
    sx,
    size = 'small',
    colorVariant = 'primary',
    slotProps,
    onChange,
    onSelect,
    onSubmit,
    onDrawOpen,
    className,
    value,
    inputValue,
    placeholder = 'Search',
    options = [],
    ...rest
  } = props;

  const ownerState = {
    size,
    colorVariant,
  };

  const [iOnSubmit, iOnChange, iOnInputChange] = useSearchField({
    value,
    inputValue,
    onChange,
    onSelect,
    onSubmit,
  });

  return (
    <SearchFieldRoot
      sx={sx}
      ownerState={ownerState}
      onSubmit={iOnSubmit}
      {...slotProps?.form}
    >
      <Autocomplete
        loadingText={<AutocompleteLoadingIndicator />}
        {...rest}
        options={options}
        value={value}
        inputValue={inputValue}
        size={size}
        renderInput={(textFieldProps) => (
          <TextField
            {...textFieldProps}
            placeholder={placeholder}
            size={size}
          />
        )}
        onInputChange={iOnInputChange}
        onChange={iOnChange}
        forcePopupIcon={Boolean(onDrawOpen)}
        popupIcon={<DrawSearch sx={{ position: 'relative', top: -1 }} />}
        clearIcon={<Close sx={{ position: 'relative', top: 1 }} />}
        freeSolo
        slotProps={{
          clearIndicator: {
            size,
            ...slotProps?.clearIndicator,
          },
          ...slotProps,
        }}
      />
      <IconButton
        className={autocompleteClasses.searchFieldSubmit}
        aria-label="Search"
        color={colorVariants.button[colorVariant]?.color || 'primary'}
        variant={colorVariants.button[colorVariant]?.variant || 'contained'}
        size={size}
        type="submit"
        {...slotProps?.submit}
      >
        <Search {...slotProps?.submitIcon} />
      </IconButton>
    </SearchFieldRoot>
  );
}

SearchField.defaultProps = {
  size: 'small',
  colorVariant: 'primary',
};

export default React.memo(SearchField);
